import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, onSnapshot, deleteField } from 'firebase/firestore';
import { getDeviceId } from '../utils/deviceIdentifier';
import { getDeviceAuthToken, signInWithCustomToken } from '../services/authService';
import { migrateAdhanSettings } from '../utils/adhanUtils';
import { fetchTranslations } from '../services/quranService';
import { FIELD_LEVEL_SIMPLE_DASHBOARD_FIELDS, stripSimpleDashboardFields } from '../utils/screenSchedule';

const SettingsContext = createContext();

const getSavedSettings = async () => {
  try {
    if (auth.currentUser) {
      // For logged in users, get settings from their user document
      const userSettingsRef = doc(db, 'userSettings', auth.currentUser.uid);
      const userSettingsDoc = await getDoc(userSettingsRef);
      if (userSettingsDoc.exists()) {
        return userSettingsDoc.data();
      }
    } else {
      // For non-logged in users, immediately return default settings
      return {
        ...defaultSettings,
        tunedTimes: defaultSettings.tunedTimes
      };
    }

    // If no settings found, return default settings
    return {
      ...defaultSettings,
      tunedTimes: defaultSettings.tunedTimes
    };
  } catch (error) {
    console.error('Error getting saved settings:', error);
    return {
      ...defaultSettings,
      tunedTimes: defaultSettings.tunedTimes
    };
  }
};

export function useSettings() {
  return useContext(SettingsContext);
}

const defaultSettings = {
  location: null,
  coordinates: null,
  selectedAdhan: null,
  calculationMethod: 2,
  tunedTimes: {
    Fajr: 0,
    Dhuhr: 0,
    Asr: 0,
    Maghrib: 0,
    Isha: 0
  },
  adhanClockLocation: {
    enabled: false,
    location: null,
    coordinates: null
  },
  hasSeenWelcome: false,
  dashboardItems: {
    dateTime: true,
    prayerTimes: true,
    dailyDua: true,
    dailyHadith: true,
    quranTracker: true,
    news: false,
    adhkarTracker: false,
    goals: true,
  },
  simpleDashboard: {
    theme: 'minimalist',
    dashboardColumns: 3,
    dashboardGap: 'gap-4',
    dashboardPadding: 'p-4',
    columnSections: {
      section1: {
        width: 3,
        backgroundImages: [],
        backgroundImage: null,
        backgroundColor: null,
        fullHeight: false,
        showBorder: true,
        components: [],
        slideshowEnabled: false,
        slideshowDuration: 60,
        overlayOpacity: 0.5,
      }
    },
    components: {
      date: {
        enabled: true,
        name: "Date",
        showTitle: true,
        showDate: true,
        showHijriDate: true,
        dateFontSize: 'text-lg',
        hijriDateFontSize: 'text-lg',
        dateAlign: 'text-center',
        hijriDateAlign: 'text-center',
      },
      prayerCountdown: {
        enabled: true,
        name: "Prayer Countdown",
        showNextPrayer: true,
        showCountdown: true,
        nextPrayerNameFontSize: 'text-xl',
        countdownFontSize: 'text-4xl',
        nextPrayerNameAlign: 'text-center',
        countdownAlign: 'text-center',
      },
      time: {
        enabled: true,
        name: "Time",
        showGreeting: false,
        timeFontSize: 'text-6xl',
        timeAlign: 'text-center',
        greetingFontSize: 'text-xl',
        greetingAlign: 'text-center',
      },
      prayerTimes: {
        enabled: true,
        name: "Prayer Times",
        showTitle: true,
        showPrayerNames: true,
        showPrayerTimes: true,
        showLocation: true,
        titleFontSize: 'text-xl',
        prayerNameFontSize: 'text-base',
        prayerTimeFontSize: 'text-base',
        locationFontSize: 'text-sm'
      },
      dailyDua: {
        enabled: true,
        name: "Daily Dua",
        showTitle: true,
        showArabic: true,
        showTranslation: true,
        showReference: true,
        showButtons: false,
        arabicFontSize: 'text-2xl',
        translationFontSize: 'text-lg',
        arabicAlign: 'text-right',
        translationAlign: 'text-left',
        referenceAlign: 'text-left',
        titleAlign: 'text-left'
      },
      dailyHadith: {
        enabled: true,
        name: "Daily Hadith",
        showTitle: true,
        showArabic: true,
        showTranslation: true,
        showSource: true,
        showButtons: false,
        arabicFontSize: 'text-2xl',
        translationFontSize: 'text-lg',
        arabicAlign: 'text-right',
        translationAlign: 'text-left',
        sourceAlign: 'text-left',
        titleAlign: 'text-left'
      },
      quranTracker: {
        enabled: true,
        name: "Quran Progress",
        width: 1
      },
      goals: {
        enabled: true,
        name: "Goals",
        width: 1
      },
      emptyColumn: {
        enabled: true,
        name: "Empty Column",
        width: 1
      }
    },
    order: [
      { type: 'component', id: 'time', enabled: true },
      { type: 'component', id: 'date', enabled: true },
      { type: 'component', id: 'prayerCountdown', enabled: true },
      { type: 'section', id: 'section1' },
      { type: 'component', id: 'emptyColumn' }
    ],
    templateId: 'adhanClock',
  },
  quran: {
    fontSize: 'text-xl',
    mode: 'reading',
    displayMode: 'with-translation',
    showTransliteration: false,
    selectedReciter: null,
    selectedLanguage: null,
    selectedTranslation: null,
    memorization: {},
    readingProgress: {
      currentSession: {
        surah: null,
        verse: null,
        timestamp: null
      },
      history: {}
    }
  },
};

// Utility for guest settings persistence
const GUEST_SETTINGS_KEY = 'ummahti_guest_settings';

const loadGuestSettings = () => {
  try {
    const data = localStorage.getItem(GUEST_SETTINGS_KEY);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
};

const saveGuestSettings = (settings) => {
  try {
    localStorage.setItem(GUEST_SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {}
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    // On first load, try to load guest settings from localStorage
    const guestSettings = loadGuestSettings();
    return guestSettings ? { ...defaultSettings, ...guestSettings } : defaultSettings;
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showNavigation, setShowNavigation] = useState(true);

  // Debug guest settings on load
  useEffect(() => {
    console.log('[SettingsContext] Initial settings state:', {
      hasLocation: !!settings.location,
      hasCoordinates: !!settings.coordinates,
      locationValue: settings.location,
      coordinatesValue: settings.coordinates
    });
  }, []);

  // Ensure default translation is set for new users (guests and logged in)
  useEffect(() => {
    async function ensureDefaultTranslation() {
      if (!settings.quran.selectedTranslation) {
        try {
          const data = await fetchTranslations();
          // Find the first English translation
          const englishTranslation = data.translations.find(
            t => t.language_name && t.language_name.toLowerCase() === 'english'
          );
          if (englishTranslation) {
            setSettings(prev => ({
              ...prev,
              quran: {
                ...prev.quran,
                selectedLanguage: 'en',
                selectedTranslation: englishTranslation.id.toString(),
              }
            }));
          }
        } catch (e) {
          console.error('Failed to set default English translation:', e);
        }
      }
    }
    ensureDefaultTranslation();
    // Only run on mount or if selectedTranslation changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.quran.selectedTranslation]);

  useEffect(() => {
    console.log('SettingsProvider initializing...');
    
    const unsubscribeAuth = auth.onAuthStateChanged(async user => {
      console.log('Auth state changed:', { 
        hasUser: !!user, 
        isWebUser: user && user.providerData.length > 0,
        isDeviceUser: user && user.providerData.length === 0 
      });
      
      if (user) {
        // User is logged in, get their settings
        const userSettingsRef = doc(db, 'userSettings', user.uid);
        const unsubscribe = onSnapshot(userSettingsRef, async (doc) => {
          if (doc.exists()) {
            const userSettings = doc.data();
            console.log('Raw user settings from Firestore:', userSettings);
            console.log('[DEBUG] Firestore userSettings.location:', userSettings.location, 'userSettings.coordinates:', userSettings.coordinates);
            // Only migrate for web users
            const settingsToUse = user.providerData.length > 0 
              ? await migrateSettings(userSettings)
              : userSettings;

            // Ensure Quran settings are properly initialized with last selected values
            const mergedSettings = {
              ...defaultSettings,
              ...settingsToUse,
              simpleDashboard: {
                ...defaultSettings.simpleDashboard,
                ...(settingsToUse.simpleDashboard || {})
              },
              quran: {
                ...defaultSettings.quran,
                ...(settingsToUse.quran || {}),
                // Use last selected values if current ones aren't set
                selectedReciter: settingsToUse.quran?.selectedReciter || settingsToUse.lastSelectedReciter,
                selectedLanguage: settingsToUse.quran?.selectedLanguage || settingsToUse.lastSelectedLanguage,
                selectedTranslation: settingsToUse.quran?.selectedTranslation || settingsToUse.lastSelectedTranslation,
              }
            };
            console.log('[DEBUG] Merged settings to be set in context:', mergedSettings);
            setSettings(mergedSettings);
          } else {
            setSettings(defaultSettings);
          }
          setIsInitialized(true);
        });

        return () => unsubscribe();
      } else {
        // Guest: load guest settings from localStorage if available
        const guestSettings = loadGuestSettings();
        setSettings(guestSettings ? { ...defaultSettings, ...guestSettings } : defaultSettings);
        setIsInitialized(true);
        return () => {};
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // Add this function to clean settings object before saving to Firestore
  const removeUndefinedValues = (obj) => {
    // Create a deep copy of the object
    const cleaned = JSON.parse(JSON.stringify(obj));
    return cleaned;
  };

  const updateSettings = async (newSettings) => {
    console.log('SettingsContext - updateSettings:', newSettings);
    const user = auth.currentUser;
    if (!user) {
      // Guest: merge and persist to localStorage
      setSettings(prev => {
        const merged = { ...prev, ...newSettings };
        saveGuestSettings(merged);
        return merged;
      });
      return;
    }

    try {
      const userSettingsRef = doc(db, 'userSettings', user.uid);
      
      // First get the current settings
      const currentDoc = await getDoc(userSettingsRef);
      const currentSettings = currentDoc.exists() ? currentDoc.data() : settings;

      console.log('SettingsContext - Current settings:', currentSettings);

      // Deep merge the settings, ensuring Quran settings are properly handled
      const mergedSettings = {
        ...currentSettings,
        ...newSettings,
        quran: {
          ...defaultSettings.quran,
          ...(currentSettings.quran || {}),
          ...(newSettings.quran || {}),
          // Explicitly preserve readingProgress if it exists and isn't being updated
          readingProgress: newSettings.quran?.readingProgress || currentSettings.quran?.readingProgress || defaultSettings.quran.readingProgress,
          // Explicitly preserve memorization if it exists and isn't being updated
          memorization: newSettings.quran?.memorization || currentSettings.quran?.memorization || defaultSettings.quran.memorization
        }
      };

      console.log('SettingsContext - Merged settings:', mergedSettings);

      // If updating Quran settings, also update the last selected values
      if (newSettings.quran) {
        if (newSettings.quran.selectedTranslation) {
          mergedSettings.lastSelectedTranslation = newSettings.quran.selectedTranslation;
        }
      }

      // Clean settings by removing undefined values
      const cleanedSettings = removeUndefinedValues(mergedSettings);

      // simpleDashboard.screenSchedule / nightBrightness are written only by
      // updateSimpleDashboardField (field-level); a merge write of the context copy could
      // revert a newer edit and merge trigger maps key by key. Keep the local copies as they are.
      await setDoc(userSettingsRef, stripSimpleDashboardFields(cleanedSettings), { merge: true });
      setSettings(prev => {
        const simpleDashboard = { ...cleanedSettings.simpleDashboard };
        for (const field of FIELD_LEVEL_SIMPLE_DASHBOARD_FIELDS) {
          simpleDashboard[field] = prev.simpleDashboard?.[field];
        }
        return { ...cleanedSettings, simpleDashboard };
      });
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  /**
   * Field-level write of one `simpleDashboard.<key>` value. Unlike updateSettings (which
   * deep-merges the whole document), `mergeFields` replaces exactly this field path, so a
   * map value is written as-is (stale nested keys don't survive) and nothing else in
   * the document is rewritten. Creates the document when it doesn't exist yet.
   */
  const updateSimpleDashboardField = async (key, value) => {
    const applyLocally = (prev) => ({
      ...prev,
      simpleDashboard: { ...(prev.simpleDashboard || {}), [key]: value },
    });
    const user = auth.currentUser;
    if (!user) {
      setSettings(prev => {
        const merged = applyLocally(prev);
        saveGuestSettings(merged);
        return merged;
      });
      return;
    }
    const userSettingsRef = doc(db, 'userSettings', user.uid);
    await setDoc(
      userSettingsRef,
      { simpleDashboard: { [key]: value } },
      { mergeFields: [`simpleDashboard.${key}`] }
    );
    setSettings(applyLocally);
  };

  const migrateSettings = (settings) => {
    // Simply return the settings without migration
    return settings;
  };

  const value = {
    settings,
    updateSettings,
    updateSimpleDashboardField,
    isInitialized,
    showSettings,
    setShowSettings,
    isSettingsOpen,
    setIsSettingsOpen,
    showNavigation,
    setShowNavigation,
    // Quran settings
    reciterOptions: [], // This should be populated from your data source
    selectedReciter: settings.lastSelectedReciter,
    handleReciterChange: (option) => {
      updateSettings({
        ...settings,
        lastSelectedReciter: option.value
      });
    },
    languageOptions: [], // This should be populated from your data source
    selectedLanguage: settings.lastSelectedLanguage,
    handleLanguageChange: (option) => {
      updateSettings({
        ...settings,
        lastSelectedLanguage: option.value
      });
    },
    translationOptions: [], // This should be populated from your data source
    selectedTranslation: settings.lastSelectedTranslation,
    handleTranslationChange: (option) => {
      updateSettings({
        ...settings,
        lastSelectedTranslation: option.value
      });
    },
    displayMode: settings.quran.displayMode,
    setDisplayMode: (mode) => {
      updateSettings({
        ...settings,
        quran: {
          ...settings.quran,
          displayMode: mode
        }
      });
    },
    showTransliteration: settings.quran.showTransliteration,
    handleTransliterationToggle: (value) => {
      updateSettings({
        ...settings,
        quran: {
          ...settings.quran,
          showTransliteration: value
        }
      });
    },
    quranMode: settings.quran.mode,
    setQuranMode: (mode) => {
      updateSettings({
        ...settings,
        quran: {
          ...settings.quran,
          mode: mode
        }
      });
    },
    fontSize: settings.quran.fontSize,
    setFontSize: (size) => {
      updateSettings({
        ...settings,
        quran: {
          ...settings.quran,
          fontSize: size
        }
      });
    },
    onResetMemorization: () => {
      updateSettings({
        ...settings,
        quran: {
          ...settings.quran,
          memorization: {} // Reset memorization data
        }
      });
    },
    onResetReadingProgress: () => {
      // Create a properly structured reading progress object
      const defaultReadingProgress = {
        currentSession: {
          surah: null,
          verse: null,
          timestamp: null
        },
        history: {}
      };

      updateSettings({
        ...settings,
        quran: {
          ...settings.quran,
          readingProgress: defaultReadingProgress
        }
      });
    },
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}
